import { Box, Chip, Skeleton, Typography } from '@mui/material';
import ModalityChip from '~/components/Chip/ModalityChip';
import { useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Image3D } from '~/domain/patient/image3d';
import { useImageThumbnail } from '~/hooks/image/useImageThumbnail';

export default function ImageThumbnailCard({
    image,
    label,
    isSelected,
    isAvailable,
    sharedFor,
    statusIcon,
    onClick,
}: {
    image: Image3D;
    label: string;
    isSelected: boolean;
    isAvailable: boolean;
    sharedFor?: boolean;
    statusIcon: React.ReactNode;
    onClick: () => void;
}) {
    const intl = useIntl();
    const dataUrl = useImageThumbnail(image);
    const modality = image.seriesMetadata.modality;
    const slices = image.grid.sizeZ;

    return (
        <Box
            onClick={() => isAvailable && onClick()}
            sx={{
                position: 'relative',
                display: 'flex',
                gap: 1,
                px: 1,
                py: 0.75,
                overflow: 'hidden',
                cursor: isAvailable ? 'pointer' : 'default',
                opacity: isAvailable ? 1 : 0.6,
                bgcolor: isSelected
                    ? 'rgba(144, 202, 249, 0.08)'
                    : 'transparent',
                '&:hover': isAvailable
                    ? {
                          bgcolor: isSelected
                              ? 'rgba(144, 202, 249, 0.12)'
                              : 'action.hover',
                      }
                    : undefined,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                    zIndex: 1,
                },
                transition: 'all 0.15s ease-in-out',
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#000',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    alignSelf: 'center',
                }}
            >
                {!dataUrl ? (
                    <Skeleton
                        variant="rectangular"
                        width={40}
                        height={40}
                        animation="wave"
                        sx={{ bgcolor: 'grey.800' }}
                    />
                ) : (
                    <Box
                        component="img"
                        src={dataUrl}
                        alt={label}
                        sx={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                        }}
                    />
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    alignSelf: 'center',
                }}
            >
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                        fontSize={11}
                        noWrap
                        sx={{ fontWeight: isSelected ? 500 : 400 }}
                    >
                        <SensitiveInfoTypography>
                            {label}
                        </SensitiveInfoTypography>
                    </Typography>
                    {statusIcon}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography fontSize={10} color="text.secondary">
                        {slices} slices
                    </Typography>
                    {modality && (
                        <ModalityChip modality={modality} size="small" />
                    )}
                    {sharedFor && (
                        <Chip
                            label={intl.formatMessage({
                                id: 'structureSet.copy.sharedFor',
                            })}
                            size="small"
                            color="primary"
                            sx={{
                                fontSize: 8,
                                fontWeight: 500,
                                lineHeight: 1,
                                maxHeight: '12px',
                                p: 0,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
